import PackageEditModalFooter from '@advisory-report/src/features/creation-flow/components/insurance-policies/current-insurances/packages/PackageEditModalFooter';
import AnvaPackageItemEditModalBody from '@flows/src/components/flow-products/quotes/basket/AnvaPackageItemEditModalBody';
import useAnvaFlowProductsBasketStore from '@flows/src/store/zustand/flows-products/useAnvaFlowProductsBasketStore';
import { AnvaQuote } from '@flows/src/types/anva';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
} from '@wegroup/design-system';
import { format } from 'date-fns';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string, number } from 'yup';

interface FormValues {
  start_date?: string;
  payment_interval: string | null;
  anva_collection_method_code: number | null | undefined;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quoteData: AnvaQuote;
  packageCode?: string;
  isAddingMode?: boolean;
  onConfirmAdd?: (values: FormValues) => void;
  onAfterSubmit?: (values: FormValues) => void;
}

const AnvaQuoteEditModal: FC<Props> = ({
  isOpen,
  onClose,
  quoteData,
  packageCode,
  isAddingMode = false,
  onConfirmAdd,
  onAfterSubmit,
}) => {
  const { t } = useTranslation();
  const [modalKey, setModalKey] = useState(0);
  const updateInsurance = useAnvaFlowProductsBasketStore(
    (state) => state.updateInsurance,
  );
  const updateInsuranceInPackage = useAnvaFlowProductsBasketStore(
    (state) => state.updateInsuranceInPackage,
  );

  // Create validation schema making payment method required
  const validationSchema = object({
    start_date: string().nullable(),
    payment_interval: string().nullable(),
    anva_collection_method_code: number()
      .typeError(t('COLLECTION-METHOD') + ' ' + t('REQUIRED_FIELD'))
      .required(t('COLLECTION-METHOD') + ' ' + t('REQUIRED_FIELD')),
  });

  const methods = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      start_date: quoteData.start_date,
      payment_interval: quoteData.payment_interval,
      anva_collection_method_code:
        quoteData.anva_collection_method_code != null
          ? String(quoteData.anva_collection_method_code)
          : undefined,
    },
  });

  // Watch payment interval for auto direct debit
  const paymentInterval = methods.watch('payment_interval');

  // Check if direct debit is required (monthly payments)
  const isDirectDebitRequired =
    paymentInterval && paymentInterval === 'MONTHLY';

  // Auto direct debit for monthly payment intervals
  useEffect(() => {
    if (isDirectDebitRequired) {
      // Set appropriate direct debit collection method based on type
      const directDebitCode = packageCode ? '48' : '28'; // PACKAGE or POLICY
      methods.setValue('anva_collection_method_code', directDebitCode);
    }
  }, [paymentInterval, methods, packageCode, isDirectDebitRequired]);

  // Reset form when modal opens or quote data changes
  useEffect(() => {
    if (isOpen) {
      setModalKey((prev) => prev + 1);
      // Get default values based on mode and context
      const getDefaultValues = () => {
        const baseValues = {
          start_date: quoteData.start_date,
          payment_interval: quoteData.payment_interval,
          anva_collection_method_code:
            quoteData.anva_collection_method_code != null
              ? String(quoteData.anva_collection_method_code)
              : undefined,
        };

        // If adding (both package and policy), set today's date as default start date if not already set
        if (isAddingMode && !quoteData.start_date) {
          const today = new Date();
          // Use yyyy-MM-dd format which DateInput expects - avoid timezone issues
          baseValues.start_date = today.toISOString().split('T')[0];
        }

        return baseValues;
      };

      methods.reset(getDefaultValues());
    }
  }, [isOpen, quoteData, isAddingMode, packageCode, methods]);

  const handleSubmit = methods.handleSubmit((values) => {
    // Handle form submission
    const formattedDueDate = values.start_date
      ? format(new Date(values.start_date), 'yyyy-MM-dd')
      : undefined;

    const formattedValues = {
      start_date: formattedDueDate,
      anva_collection_method_code:
        values.anva_collection_method_code != null
          ? Number(values.anva_collection_method_code)
          : undefined,
    };

    if (isAddingMode && onConfirmAdd) {
      // Adding mode: call the callback with the form values
      onConfirmAdd({
        ...values,
        ...formattedValues,
      });
    } else if (packageCode) {
      // Editing mode: update insurance in package
      updateInsuranceInPackage(
        packageCode,
        quoteData.anva_product_id,
        quoteData.index,
        {
          ...formattedValues,
          payment_interval: values.payment_interval,
        },
      );
    } else {
      // Editing mode: update standalone insurance
      updateInsurance(quoteData.anva_product_id, quoteData.index, {
        ...formattedValues,
        payment_interval: values.payment_interval,
      });
    }

    onAfterSubmit?.({
      ...values,
      ...formattedValues,
    });

    onClose();
  });

  // Generate appropriate modal title
  const getModalTitle = () => {
    const insuranceName = quoteData.name;

    if (isAddingMode) {
      if (packageCode) {
        // Find the package name from possible packages
        const packageInfo = quoteData.possible_packages.find(
          (pkg) => pkg.code === packageCode,
        );
        const packageName = packageInfo?.name || packageCode;
        return `${t(
          'ADVISORY-REPORTS_CREATION-FLOW_PACKAGE_MODAL_TITLE',
        )} - ${packageName}`;
      }
      return `${t('Insurance info')} - ${insuranceName}`;
    }
    return `${t('Insurance info')} - ${insuranceName}`;
  };

  // Custom close handler to prevent closing when form is invalid and in adding mode
  const handleClose = () => {
    if (isAddingMode) {
      // In adding mode, only allow closing if the form is valid or force close
      const isValid = methods.formState.isValid;
      const hasPaymentMethod = !!methods.getValues(
        'anva_collection_method_code',
      );

      if (!isValid || !hasPaymentMethod) {
        // Don't close, trigger validation to show errors
        methods.trigger();
        return;
      }
    }
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={!isAddingMode}
      closeOnEsc={!isAddingMode}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{getModalTitle()}</ModalHeader>
        {!isAddingMode && <ModalCloseButton />}
        <FormProvider {...methods}>
          <AnvaPackageItemEditModalBody
            key={modalKey}
            hiddenFields={
              packageCode && !isAddingMode
                ? ['anva_collection_method_code', 'payment_interval']
                : packageCode && isAddingMode
                ? ['payment_interval']
                : []
            }
            type={packageCode ? 'PACKAGE' : 'POLICY'}
            isPaymentMethodRequired={isAddingMode}
          />
        </FormProvider>
        <PackageEditModalFooter
          onSubmit={handleSubmit}
          onClose={onClose}
          isDisabled={!isAddingMode && !methods.formState.isDirty}
        />
      </ModalContent>
    </Modal>
  );
};

export default AnvaQuoteEditModal;
